import React from 'react'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'

import NumbersIcon from '@mui/icons-material/Numbers'
import { ICharacterItem } from '../../../../../../../../api/types/character'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  List,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Item } from '../../../../../../../ItemsIndex/components/Item/Item'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { createStyles, makeStyles } from '@mui/styles'
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew'
import { EquipSlotTranslation, IItem } from '../../../../../../../../api/types/item'
import { CharacterItemActions } from './CharacterItemActions/CharacterItemActions'
import { fromAxiosError } from '../../../../../../../../context/GlobalError'
import { useQuery } from '@tanstack/react-query'
import {
  characterInventoryQueryKey,
  queryCharacterInventory,
} from '../../../../../../../../api/queries'
import { ErrorHandler } from '../../../../../../../../components/ErrorHandler/ErrorHandler'
import cx from 'classnames'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleBackground: {
      backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#d0d0d0',
    },
    mr4: {
      marginRight: '4px',
    },
    blue: {
      color: theme.palette.mode === 'dark' ? '#3850f1' : '#2139B7FF',
    },
    yellow: {
      color: theme.palette.mode === 'dark' ? '#f1ee38' : '#828f10',
    },
  })
)

export const CharacterInventory: React.FC<{ char_id: number; game_id?: number }> = ({
  char_id,
  game_id,
}) => {
  const classes = useStyles()

  const {
    isLoading: isLoading,
    data: items,
    error,
  } = useQuery<ICharacterItem[]>(characterInventoryQueryKey(char_id), () =>
    queryCharacterInventory(char_id)
  )

  return (
    <>
      <ErrorHandler loading={isLoading} error={fromAxiosError(error)}>
        <List>
          {items &&
            items.map(character_item => (
              <Accordion elevation={0}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.titleBackground}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>
                      {character_item.identified || (
                        <>
                          <Tooltip title={'Niezidentyfikowany'}>
                            <QuestionMarkIcon fontSize={'small'} />
                          </Tooltip>
                          <Typography>{character_item.item.name}</Typography>
                        </>
                      )}

                      {character_item.identified && (
                        <Typography
                          className={cx(
                            character_item.item.quality === 'magic' && classes.blue,
                            ['magic', 'common'].includes(character_item.item.quality) ||
                              classes.yellow
                          )}
                        >
                          {character_item.item.name}
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <>
                        {character_item.equipped && (
                          <Tooltip title={'Założony'}>
                            <Chip
                              className={classes.mr4}
                              icon={<AccessibilityNewIcon />}
                              label={
                                EquipSlotTranslation[character_item.item.equip_slot] +
                                (character_item.item.two_handed ? ' (2)' : '')
                              }
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {character_item.amount > 0 && (
                          <Tooltip title={'Ilość'}>
                            <Chip
                              className={classes.mr4}
                              icon={<NumbersIcon />}
                              label={character_item.amount}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {character_item.item.charges > 0 && (
                          <Tooltip title={'Pozostałe ładunki'}>
                            <Chip
                              className={classes.mr4}
                              icon={<AutoFixHighIcon />}
                              label={character_item.charges}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '0px', margin: '0px' }}>
                  <Item
                    maxWidth={1000}
                    item={character_item.item}
                    showActions={false}
                    elevation={0}
                    showTitle={false}
                    additionalActions={(_item: IItem) => (
                      <>
                        {game_id && (
                          <CharacterItemActions game_id={game_id} character_item={character_item} />
                        )}
                      </>
                    )}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
        </List>
      </ErrorHandler>
    </>
  )
}
